import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { SHEET_PANEL } from './panel';

interface NotificationItem {
  id: string;
  kind: 'finding' | 'billing' | 'olt';
  tone: 'info' | 'warn' | 'danger';
  title: string;
  detail: string;
  href: string;
  at: number;
  unread: boolean;
}

/**
 * The bell beside the theme switch: what this user has not seen yet.
 *
 * It shows the things the platform already recorded — open findings from the
 * OLT sweeps, and a bill about to fall due — rather than a separate stream
 * that could disagree with them. A finding that is fixed or dismissed leaves
 * the list because it is no longer open, not because anything cleared it.
 *
 * The badge counts UNREAD only; the panel shows recent items either way, so
 * clearing the bell never hides what is still going on.
 */
const TONE_DOT = {
  info: 'bg-info',
  warn: 'bg-warn',
  danger: 'bg-danger',
} as const;

export function NotificationsBell() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: NotificationItem[]; unread: number }>('/notifications'),
    refetchInterval: 60_000,
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: () => api.post('/notifications/read'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Click-away and Escape: a panel that traps the page is worse than no panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        title={unread > 0 ? `${unread} unread` : 'Notifications'}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
        className="relative flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-ink"
      >
        <Bell className="size-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-4 text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`z-50 overflow-hidden rounded-xl border border-line bg-surface shadow-2xl ${SHEET_PANEL} sm:top-10 sm:w-80`}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="text-sm font-semibold text-ink">
              Notifications
              {unread > 0 && <span className="ml-1.5 text-xs font-normal text-faint">{unread} unread</span>}
            </span>
            {unread > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                onClick={() => markRead.mutate()}
              >
                <CheckCheck className="size-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted">Nothing to report.</p>
          ) : (
            <ul className="max-h-96 divide-y divide-line overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <Link
                    to={n.href}
                    onClick={() => setOpen(false)}
                    className={`flex gap-2.5 px-4 py-3 transition-colors hover:bg-surface-2 ${
                      n.unread ? '' : 'opacity-70'
                    }`}
                  >
                    <span className={`mt-1.5 size-2 shrink-0 rounded-full ${TONE_DOT[n.tone]}`} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">{n.title}</span>
                      <span className="block text-xs text-muted">{n.detail}</span>
                      <span className="block text-[11px] text-faint">{formatRelative(n.at)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
